/**
 * 메시지 아이템 컴포넌트
 * URL/패스워드 복사 기능 포함
 */

import React, { useState } from 'react';
import { Copy, Check, ExternalLink } from 'lucide-react';

// 복사 버튼 컴포넌트
function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('복사 실패:', err);
    }
  };

  return (
    <button
      className="copy-btn"
      onClick={handleCopy}
      title={`${label} 복사`}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      <span>{copied ? '복사됨' : label}</span>
    </button>
  );
}

// URL 감지 및 렌더링
function renderContentWithLinks(content) {
  // URL 패턴
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  // 패스워드/아이디 패턴 (비밀번호: xxx, 아이디: xxx)
  const passwordPattern = /비밀번호:\s*([^\s,\n]+)/g;
  const usernamePattern = /아이디:\s*([^\s,\n]+)/g;
  const cardPattern = /카드번호:\s*([^\s,\n]+)/g;
  const cvvPattern = /CVV:\s*([^\s,\n]+)/g;
  const apiKeyPattern = /API\s*키:\s*([^\s,\n]+)/g;

  const parts = [];
  let lastIndex = 0;
  let match;

  // URL 처리
  const urlRegex = new RegExp(urlPattern);
  while ((match = urlRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: content.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'url', value: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push({ type: 'text', value: content.slice(lastIndex) });
  }

  // 부분별로 추가 패턴 처리
  return parts.map((part, idx) => {
    if (part.type === 'url') {
      return (
        <span key={idx} className="message-url">
          <a href={part.value} target="_blank" rel="noopener noreferrer">
            {part.value}
            <ExternalLink size={12} />
          </a>
          <CopyButton text={part.value} label="URL" />
        </span>
      );
    }

    // 텍스트에서 민감 정보 추출
    let text = part.value;
    const sensitiveItems = [];

    // 비밀번호 추출
    const pwMatches = [...text.matchAll(/비밀번호:\s*([^\s,\n]+)/g)];
    pwMatches.forEach(m => sensitiveItems.push({ label: '비밀번호', value: m[1] }));

    // 아이디 추출
    const idMatches = [...text.matchAll(/아이디:\s*([^\s,\n]+)/g)];
    idMatches.forEach(m => sensitiveItems.push({ label: '아이디', value: m[1] }));

    // 카드번호 추출
    const cardMatches = [...text.matchAll(/카드번호:\s*([^\s,\n]+)/g)];
    cardMatches.forEach(m => sensitiveItems.push({ label: '카드번호', value: m[1] }));

    // CVV 추출
    const cvvMatches = [...text.matchAll(/CVV:\s*([^\s,\n]+)/g)];
    cvvMatches.forEach(m => sensitiveItems.push({ label: 'CVV', value: m[1] }));

    // API 키 추출
    const apiMatches = [...text.matchAll(/API\s*키:\s*([^\s,\n]+)/g)];
    apiMatches.forEach(m => sensitiveItems.push({ label: 'API 키', value: m[1] }));

    return (
      <span key={idx}>
        {text}
        {sensitiveItems.length > 0 && (
          <span className="copy-buttons">
            {sensitiveItems.map((item, i) => (
              <CopyButton key={i} text={item.value} label={item.label} />
            ))}
          </span>
        )}
      </span>
    );
  });
}

// 소스 표시
function SourceBadge({ source }) {
  const typeLabels = {
    password: '패스워드',
    creditCard: '신용카드',
    apiKey: 'API 키',
    note: '메모',
    knowledge: '지식'
  };

  const typeColors = {
    password: '#3b82f6',
    creditCard: '#8b5cf6',
    apiKey: '#f59e0b',
    note: '#10b981',
    knowledge: '#6366f1'
  };

  return (
    <span
      className="source-badge"
      style={{ backgroundColor: typeColors[source.type] || '#6b7280' }}
    >
      {typeLabels[source.type] || source.type}
    </span>
  );
}

// 메시지 아이템 컴포넌트
function MessageItem({ message }) {
  const { role, content, timestamp, sources, isError } = message;

  const avatars = {
    user: '👤',
    assistant: '🤖',
    system: '✅'
  };

  return (
    <div className={`message ${role} ${isError ? 'error' : ''}`}>
      <div className="message-avatar">
        {avatars[role] || '💬'}
      </div>
      <div className="message-content">
        <div className="message-text">
          {renderContentWithLinks(content)}
        </div>

        {/* 소스 표시 */}
        {sources && sources.length > 0 && (
          <div className="message-sources">
            <span className="sources-label">출처:</span>
            {sources.map((source, idx) => (
              <SourceBadge key={idx} source={source} />
            ))}
          </div>
        )}

        <div className="message-time">
          {timestamp instanceof Date
            ? timestamp.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })
            : new Date(timestamp).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
}

export default MessageItem;
